import yaml from 'js-yaml'

export type HttpMethod = 'get' | 'post' | 'put' | 'delete' | 'patch'

export interface SpecParameter {
  name: string
  in: 'path' | 'query' | 'header'
  required: boolean
  description?: string
  type: string
  default?: unknown
  example?: unknown
}

export interface SpecProperty {
  name: string
  type: string
  required: boolean
  description?: string
  default?: unknown
  example?: unknown
}

export interface SpecResponse {
  status: string
  description: string
}

export interface SpecEndpoint {
  method: HttpMethod
  path: string
  operationId: string
  summary: string
  description?: string
  tags: string[]
  requiresAuth: boolean
  parameters: SpecParameter[]
  requestBody?: {
    required: boolean
    properties: SpecProperty[]
  }
  responses: SpecResponse[]
}

export interface ParsedSpec {
  info: { title: string; version: string; description?: string }
  servers: { url: string; description?: string }[]
  tags: { name: string; description?: string }[]
  endpointsByTag: Record<string, SpecEndpoint[]>
}

export async function fetchAndParseSpec(url: string): Promise<ParsedSpec> {
  const res = await fetch(url)
  const text = await res.text()
  return parseSpec(yaml.load(text) as Record<string, unknown>)
}

function parseSpec(raw: Record<string, unknown>): ParsedSpec {
  const info = raw.info as any
  const globalSecurity = (raw.security as any[]) ?? []
  const endpoints: SpecEndpoint[] = []

  for (const [path, pathItem] of Object.entries((raw.paths as any) ?? {})) {
    for (const [method, op] of Object.entries(pathItem as any)) {
      if (!['get', 'post', 'put', 'delete', 'patch'].includes(method)) continue
      const operation = op as any

      // Auth: operation-level security overrides global; [] means public
      const opSecurity: unknown[] | undefined = operation.security
      const requiresAuth = opSecurity !== undefined
        ? opSecurity.length > 0
        : globalSecurity.length > 0

      const parameters: SpecParameter[] = ((operation.parameters ?? []) as any[]).map((p: any) => ({
        name: p.name,
        in: p.in,
        required: p.required ?? p.in === 'path',
        description: p.description,
        type: p.schema?.type ?? 'string',
        default: p.schema?.default,
        example: p.schema?.example,
      }))

      let requestBody: SpecEndpoint['requestBody']
      if (operation.requestBody) {
        const schema = operation.requestBody.content?.['application/json']?.schema ?? {}
        const required: string[] = schema.required ?? []
        const properties: SpecProperty[] = Object.entries(schema.properties ?? {}).map(
          ([name, prop]: [string, any]) => ({
            name,
            type: prop.type ?? 'string',
            required: required.includes(name),
            description: prop.description,
            default: prop.default,
            example: prop.example,
          })
        )
        requestBody = { required: operation.requestBody.required ?? false, properties }
      }

      const responses: SpecResponse[] = Object.entries(operation.responses ?? {}).map(
        ([status, res]: [string, any]) => ({ status, description: res.description ?? '' })
      )

      endpoints.push({
        method: method as HttpMethod,
        path,
        operationId: operation.operationId ?? `${method}-${path}`,
        summary: operation.summary ?? '',
        description: operation.description,
        tags: operation.tags ?? ['Other'],
        requiresAuth,
        parameters,
        requestBody,
        responses,
      })
    }
  }

  // Group by tag in tag-declaration order
  const tags: { name: string; description?: string }[] = (raw.tags as any[]) ?? []
  const endpointsByTag: Record<string, SpecEndpoint[]> = {}
  for (const tag of tags) {
    endpointsByTag[tag.name] = endpoints.filter(e => e.tags.includes(tag.name))
  }
  // Endpoints with tags not declared at the top level
  for (const ep of endpoints) {
    for (const tag of ep.tags) {
      if (!endpointsByTag[tag]) endpointsByTag[tag] = []
      if (!endpointsByTag[tag].includes(ep)) endpointsByTag[tag].push(ep)
    }
  }

  return {
    info: { title: info?.title ?? '', version: info?.version ?? '', description: info?.description },
    servers: (raw.servers as any[]) ?? [],
    tags,
    endpointsByTag,
  }
}
